import { FormEvent, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import {
  loginCheck,
  rememberPassword,
  rememberPasswordCode,
  rememberPasswordNew,
} from "../../Services/Auth";
import { useNavigate } from "react-router-dom";
import { checkPerfilEstablishment, checkPerfilAdmin, Logout } from "../../Components/Auth/isAuthenticated";
import {
  LoginPageWrapper,
  LoginImagePanel,
  LoginFormPanel,
  TopBar,
  LanguageButton,
  LoginFormContent,
  LogoWrapper,
  LogoImage,
  WelcomeTitle,
  WelcomeSubtitle,
  InputGroup,
  InputGroupLabel,
  InputRequired,
  InputWrapper,
  InputIconWrapper,
  StyledInput,
  PasswordToggleBtn,
  EnterButton,
  ForgotLink,
  BackLink,
  DeveloperCredit,
  LoginA,
} from "./stylesLogin";
import { useT, useLang, LANGS, LANG_NAME } from "../../i18n";

// ─── Inline SVG Icons ──────────────────────────────────────────────────────────

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

// ─── Component ─────────────────────────────────────────────────────────────────

export const Login = () => {
  const navigate = useNavigate();
  const t = useT();
  const { lang, setLang } = useLang();

  // O seletor do topo já existia no layout, mas era decorativo. A escolha
  // feita aqui vale para o painel inteiro depois do login: fica no
  // localStorage, e o Logout() é quem a limpa.
  const [langOpen, setLangOpen] = useState(false);
  const langBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fechar = (e: MouseEvent) => {
      if (langBoxRef.current && !langBoxRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", fechar);
    return () => document.removeEventListener("mousedown", fechar);
  }, []);
  const [username, setUsername] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [repeatNewPassword, setRepeatNewPassword] = useState<string>("");

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatNewPassword, setShowRepeatNewPassword] = useState(false);

  const [showForgotPassword, setShowForgotShowPassword] = useState(false);
  const [showForgotPasswordRequested, setShowForgotShowPasswordRequested] =
    useState(false);
  const [code, setCode] = useState<string>("");
  const [codeValid, setCodeValid] = useState<boolean>(false);
  const [newPasswordCode, setNewPasswordCode] = useState<boolean>(false);

  // ─── Login com redirect baseado no role ─────────────────────────────────────
  const login = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    loginCheck(username, password)
      .then(() => {
        const token = localStorage.getItem("token");
        if (!token) {
          setIsLoading(false);
          return;
        }

        // Redireciona conforme o perfil. Este painel é só para
        // administradores e estabelecimentos.
        if (checkPerfilAdmin()) {
          return navigate("/home", { replace: true });
        }
        if (checkPerfilEstablishment()) {
          // veio do cadastro de um plano que exige cartão: leva direto
          // para a Assinatura em vez do dashboard
          const redirectTo = localStorage.getItem("postLoginRedirect");
          if (redirectTo) {
            localStorage.removeItem("postLoginRedirect");
            return navigate(redirectTo, { replace: true });
          }
          return navigate("/home_establishment", { replace: true });
        }

        // Usuário autenticou, mas não tem perfil válido para este painel
        // (ex.: usuário comum do app mobile). Bloqueia e avisa.
        toast.error(
          t("Sua conta não tem acesso a este painel. Use o aplicativo Pertalks.")
        );
        Logout(true);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const forgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    rememberPassword(username)
      .then(() => {
        setShowForgotShowPasswordRequested(true);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const forgotPasswordNew = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    rememberPasswordNew(code, password, repeatNewPassword)
      .then(() => {
        setShowForgotShowPasswordRequested(false);
        setCodeValid(false);
        setNewPasswordCode(false);
        setShowRepeatNewPassword(false);
        setShowPassword(false);
        setShowForgotShowPassword(false);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // overflow hidden apenas em desktop — no mobile bloqueia o scroll quando o teclado abre
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (code.length >= 4) {
      setCodeValid(true);
      rememberPasswordCode(code)
        .then(() => {
          setCodeValid(true);
          setNewPasswordCode(true);
        })
        .catch(() => {
          setCodeValid(false);
        });
    }
  }, [code]);

  // ─── Shared top bar ──────────────────────────────────────────────────────────
  const topBar = (
    <TopBar>
      <div ref={langBoxRef} style={{ position: "relative" }}>
        <LanguageButton
          type="button"
          onClick={() => setLangOpen((aberto) => !aberto)}
          aria-haspopup="listbox"
          aria-expanded={langOpen}
        >
          <GlobeIcon />
          {LANG_NAME[lang]}
          <ChevronDownIcon />
        </LanguageButton>

        {langOpen && (
          <div
            role="listbox"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              minWidth: 150,
              background: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 12,
              boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
              overflow: "hidden",
              zIndex: 20,
            }}
          >
            {LANGS.map((opcao) => (
              <button
                key={opcao}
                type="button"
                role="option"
                aria-selected={opcao === lang}
                onClick={() => {
                  setLang(opcao);
                  setLangOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "10px 14px",
                  border: "none",
                  textAlign: "left",
                  fontSize: 13,
                  fontWeight: opcao === lang ? 700 : 500,
                  color: opcao === lang ? "#00b6bc" : "#444",
                  background: opcao === lang ? "#e6f9fa" : "#fff",
                  cursor: "pointer",
                }}
              >
                {LANG_NAME[opcao]}
              </button>
            ))}
          </div>
        )}
      </div>
    </TopBar>
  );

  // ─── LOGIN FORM ──────────────────────────────────────────────────────────────
  const loginForm = (
    <>
      {topBar}
      <LoginFormContent>
        <LogoWrapper>
          <LogoImage src="/Pertalks2.png" alt="Pertalks" />
        </LogoWrapper>

        <WelcomeTitle>{t("Olá, como vai?")}</WelcomeTitle>
        <WelcomeSubtitle>{t("Digite seus dados para continuar.")}</WelcomeSubtitle>

        <form onSubmit={login}>
          <InputGroup>
            <InputGroupLabel htmlFor="username">
              {t("Usuário")} <InputRequired>*</InputRequired>
            </InputGroupLabel>
            <InputWrapper>
              <InputIconWrapper><UserIcon /></InputIconWrapper>
              <StyledInput
                id="username"
                type="text"
                placeholder={t("Informe seu e-mail")}
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </InputWrapper>
          </InputGroup>

          <InputGroup>
            <InputGroupLabel htmlFor="password">
              {t("Senha")} <InputRequired>*</InputRequired>
            </InputGroupLabel>
            <InputWrapper>
              <InputIconWrapper><LockIcon /></InputIconWrapper>
              <StyledInput
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder={t("Digite sua senha")}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                maxLength={30}
              />
              <PasswordToggleBtn type="button" onClick={() => setShowPassword((s) => !s)} tabIndex={-1}>
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </PasswordToggleBtn>
            </InputWrapper>
          </InputGroup>

          <EnterButton type="submit" disabled={!(username !== "" && password !== "")}>
            {isLoading ? <CircularProgress size={18} color="inherit" /> : t("Entrar")}
          </EnterButton>
        </form>

        <ForgotLink onClick={() => setShowForgotShowPassword(true)}>
          {t("Esqueci a senha")}
        </ForgotLink>

        <ForgotLink
          onClick={() => navigate("/register")}
        >
          {t("Cadastrar meu estabelecimento")}
        </ForgotLink>
      </LoginFormContent>
    </>
  );

  // ─── FORGOT PASSWORD — REQUEST ───────────────────────────────────────────────
  const forgotPasswordForm = (
    <>
      {topBar}
      <LoginFormContent>
        <BackLink onClick={() => setShowForgotShowPassword(false)}>
          <ArrowLeftIcon /> {t("Voltar")}
        </BackLink>

        <LogoWrapper>
          <LogoImage src="/Pertalks2.png" alt="Pertalks" />
        </LogoWrapper>

        <WelcomeTitle>{t("Recuperar senha")}</WelcomeTitle>
        <WelcomeSubtitle>{t("Informe seu usuário para receber o código.")}</WelcomeSubtitle>

        <form onSubmit={forgotPassword}>
          <InputGroup>
            <InputGroupLabel htmlFor="username-forgot">
              {t("Usuário")} <InputRequired>*</InputRequired>
            </InputGroupLabel>
            <InputWrapper>
              <InputIconWrapper><UserIcon /></InputIconWrapper>
              <StyledInput
                id="username-forgot"
                type="text"
                placeholder={t("Informe seu e-mail")}
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </InputWrapper>
          </InputGroup>

          <EnterButton type="submit" disabled={!username}>
            {isLoading ? <CircularProgress size={18} color="inherit" /> : t("Solicitar troca de senha")}
          </EnterButton>
        </form>
      </LoginFormContent>
    </>
  );

  // ─── FORGOT PASSWORD — VALIDATE CODE ────────────────────────────────────────
  const validateCodeForm = (
    <>
      {topBar}
      <LoginFormContent>
        <BackLink onClick={() => setShowForgotShowPasswordRequested(!showForgotPasswordRequested)}>
          <ArrowLeftIcon /> {t("Solicitar novo código")}
        </BackLink>

        <LogoWrapper>
          <LogoImage src="/Pertalks2.png" alt="Pertalks" />
        </LogoWrapper>

        <WelcomeTitle>{t("Validar código")}</WelcomeTitle>
        <WelcomeSubtitle>{t("Digite o código recebido por e-mail.")}</WelcomeSubtitle>

        <form onSubmit={forgotPasswordNew}>
          <InputGroup>
            <InputGroupLabel htmlFor="code">
              {t("Código")} <InputRequired>*</InputRequired>
            </InputGroupLabel>
            <InputWrapper>
              <StyledInput
                id="code"
                type="text"
                placeholder={t("Digite o código")}
                autoComplete="off"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={codeValid}
              />
            </InputWrapper>
          </InputGroup>

          {newPasswordCode && (
            <>
              <InputGroup>
                <InputGroupLabel htmlFor="newPassword">
                  {t("Nova Senha")} <InputRequired>*</InputRequired>
                </InputGroupLabel>
                <InputWrapper>
                  <InputIconWrapper><LockIcon /></InputIconWrapper>
                  <StyledInput
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("Digite a nova senha")}
                    autoComplete="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    maxLength={30}
                  />
                  <PasswordToggleBtn type="button" onClick={() => setShowPassword((s) => !s)} tabIndex={-1}>
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </PasswordToggleBtn>
                </InputWrapper>
              </InputGroup>

              <InputGroup>
                <InputGroupLabel htmlFor="repeatNewPassword">
                  {t("Repita a Senha")} <InputRequired>*</InputRequired>
                </InputGroupLabel>
                <InputWrapper>
                  <InputIconWrapper><LockIcon /></InputIconWrapper>
                  <StyledInput
                    id="repeatNewPassword"
                    type={showRepeatNewPassword ? "text" : "password"}
                    placeholder={t("Repita a nova senha")}
                    autoComplete="off"
                    value={repeatNewPassword}
                    onChange={(e) => setRepeatNewPassword(e.target.value)}
                    minLength={8}
                    maxLength={30}
                  />
                  <PasswordToggleBtn type="button" onClick={() => setShowRepeatNewPassword((s) => !s)} tabIndex={-1}>
                    {showRepeatNewPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </PasswordToggleBtn>
                </InputWrapper>
              </InputGroup>
            </>
          )}

          <EnterButton type="submit" disabled={!(password === repeatNewPassword && password !== "")}>
            {isLoading ? <CircularProgress size={18} color="inherit" /> : t("Redefinir senha")}
          </EnterButton>
        </form>

      </LoginFormContent>
    </>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────
  const activeForm = () => {
    if (!showForgotPassword) return loginForm;
    if (!showForgotPasswordRequested) return forgotPasswordForm;
    return validateCodeForm;
  };

  return (
    <LoginPageWrapper>
      <LoginImagePanel>
        <img src="/bg-prelogin-02.jpg" alt="" />
      </LoginImagePanel>
      <LoginFormPanel>
        {activeForm()}
      </LoginFormPanel>
    </LoginPageWrapper>
  );
};